import {
  BOT_CATALOG,
  BotCategory,
  botById,
  type MetricSeries,
  type TrafficCategory,
  type TrafficPoint,
  type TrafficSeriesResponse,
} from '@bartizo/shared';

/** What the Traffic page (P6.5) makes of the API's traffic answers. */

export const CATEGORY_LABELS: Readonly<Record<TrafficCategory, string>> = {
  browser: 'Likely human',
  search: 'Search engine',
  ai: 'AI crawler',
  seo: 'SEO tool',
  social: 'Link preview',
  monitoring: 'Monitor',
  feed: 'Feed reader',
  automation: 'Script or tool',
  other_bot: 'Other bot',
  fake_bot: 'Fake bot',
};

/** A bot's name from the catalog; `fake:<id>` is a request that claimed it falsely. */
export function botLabel(key: string): string {
  const fake = key.startsWith('fake:');
  const id = fake ? key.slice(5) : key;
  const name = botById(BOT_CATALOG, id)?.name ?? id;
  return fake ? `Claimed ${name}` : name;
}

/** Every bucket from the first with traffic to the last, a quiet one as zero. */
function filled(data: TrafficSeriesResponse): TrafficPoint[] {
  const step = data.bucketSeconds * 1000;
  const byTime = new Map(data.points.map((point) => [Date.parse(point.at), point]));
  const times = [...byTime.keys()].sort((a, b) => a - b);
  const first = times[0];
  const last = times[times.length - 1];
  if (first === undefined || last === undefined) return [];
  const out: TrafficPoint[] = [];
  for (let at = first; at <= last; at += step) {
    out.push(
      byTime.get(at) ?? {
        at: new Date(at).toISOString(),
        requests: 0,
        bytes: 0,
        status4xx: 0,
        status5xx: 0,
        categories: {},
      },
    );
  }
  return out;
}

const BOT_CATEGORIES: readonly TrafficCategory[] = BotCategory.options;

/** The chart's four lines, in MetricChart's shape. */
export function trafficLines(data: TrafficSeriesResponse): MetricSeries[] {
  const points = filled(data);
  const line = (name: string, value: (point: TrafficPoint) => number): MetricSeries => ({
    name: 'web_requests_count',
    labels: { line: name },
    points: points.map((point) => {
      const each = value(point);
      return { at: point.at, avg: each, min: each, max: each, samples: 1 };
    }),
    trend: null,
  });
  return [
    line('All requests', (point) => point.requests),
    line('Likely human', (point) => point.categories.browser ?? 0),
    line('Bots', (point) =>
      BOT_CATEGORIES.reduce((sum, category) => sum + (point.categories[category] ?? 0), 0),
    ),
    line('Fake bots', (point) => point.categories.fake_bot ?? 0),
  ];
}

/**
 * What a site's host says about who asked for it (P11.7). The log's `$host` is
 * the name a visitor asked for, or the server's own when none matched — and a
 * request that names no real site is, nearly always, somebody scanning.
 */
export interface HostLabel {
  name: string;
  /** Said under the name when the host is not an ordinary site. */
  note?: string;
  /** Not a real site: worth a look before anything else. */
  odd: boolean;
}

export function hostLabel(host: string): HostLabel {
  if (host === '_' || host === '' || host === '-') {
    return {
      name: 'No site matched',
      note: 'Asked for a name none of the sites answers to — nearly always scanners',
      odd: true,
    };
  }
  if (/^[0-9.]+$/.test(host) || host.includes(':') || host.startsWith('[')) {
    return {
      name: host,
      note: 'Asked for by address, not by name — typical of scanners',
      odd: true,
    };
  }
  if (!host.includes('.') || host === 'localhost') {
    return { name: host, note: 'Not a public site name', odd: true };
  }
  return { name: host, odd: false };
}
